import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Slider } from "../components/ui/slider";
import { toast } from "sonner";
import { ArrowRight, ArrowCounterClockwise, Flask } from "@phosphor-icons/react";

function Delta({ value, pct }) {
  if (Math.abs(value) < 0.005) return <span className="text-slate-400 text-xs">sem alteração</span>;
  const up = value > 0;
  return <span className={`text-xs font-semibold font-mono-nums ${up ? "text-rose-600" : "text-emerald-600"}`}>{up ? "+" : ""}{formatCurrency(value)} ({up ? "+" : ""}{pct}%)</span>;
}

function HourCard({ title, data, accent }) {
  return (
    <div className={`card-crisp p-5 ${accent ? "ring-2 ring-indigo-500" : ""}`}>
      <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{title}</div>
      <div className="font-mono-nums text-2xl font-extrabold text-slate-900 mt-1">{formatCurrency(data.clinical_hour_cost)}<span className="text-sm text-slate-500 font-normal">/hora</span></div>
      <div className="text-xs text-slate-500 mt-1">{formatCurrency(data.monthly_overhead)} de custos ÷ {data.hours}h</div>
    </div>
  );
}

export default function SimulatorPage() {
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef();

  const run = useCallback(async (f) => {
    setLoading(true);
    try {
      const r = await api.post("/simulate", f || {});
      setData(r.data);
      if (!f) {
        setForm({ monthly_clinical_hours: r.data.settings.monthly_clinical_hours, desired_profit_margin: r.data.settings.desired_profit_margin,
          tax_percentage: r.data.settings.tax_percentage, card_fee_percentage: r.data.settings.card_fee_percentage,
          fixed_costs: Object.fromEntries(r.data.fixed_costs.map((c) => [c.id, c.amount])), extra_fixed: 0, variable_pct_change: 0, employees_pct_change: 0 });
      }
    } catch (e) { toast.error(formatApiError(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { run(); }, [run]);

  const change = (patch) => {
    const next = { ...form, ...patch };
    setForm(next);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => run({ ...next, fixed_costs: Object.fromEntries(Object.entries(next.fixed_costs).map(([k, v]) => [k, parseFloat(v) || 0])),
      monthly_clinical_hours: parseFloat(next.monthly_clinical_hours) || 1, desired_profit_margin: parseFloat(next.desired_profit_margin) || 0,
      tax_percentage: parseFloat(next.tax_percentage) || 0, card_fee_percentage: parseFloat(next.card_fee_percentage) || 0,
      extra_fixed: parseFloat(next.extra_fixed) || 0, variable_pct_change: parseFloat(next.variable_pct_change) || 0, employees_pct_change: parseFloat(next.employees_pct_change) || 0 }), 350);
  };

  const reset = () => { setForm(null); run(); };

  if (!data || !form) return <div className="text-slate-500">Carregando...</div>;

  const num = (label, k, step = 1, suffix) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <div className="relative mt-1">
        <Input type="number" step={step} value={form[k]} onChange={(e) => change({ [k]: e.target.value })} className="font-mono-nums pr-8" data-testid={`sim-${k}`} />
        {suffix && <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-slate-500">{suffix}</span>}
      </div>
    </div>
  );

  return (
    <div data-testid="simulator-page">
      <PageHeader title="Simulador de cenários" subtitle="Mude aluguel, horas, folha ou margem e veja na hora o impacto em cada preço. Nada é salvo — é só simulação."
        action={<Button variant="outline" onClick={reset} data-testid="sim-reset"><ArrowCounterClockwise size={16} className="mr-1" /> Voltar ao real</Button>} />

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="card-crisp p-6 space-y-5 lg:col-span-1 h-fit">
          <div className="flex items-center gap-2 text-indigo-700 font-semibold"><Flask size={18} weight="duotone" /> E se...</div>
          <div>
            <div className="flex justify-between"><Label className="text-xs">Horas clínicas por mês</Label><span className="font-mono-nums text-sm font-semibold">{form.monthly_clinical_hours}h</span></div>
            <Slider className="mt-3" min={20} max={300} step={5} value={[parseFloat(form.monthly_clinical_hours) || 0]} onValueChange={([v]) => change({ monthly_clinical_hours: v })} data-testid="sim-hours-slider" />
          </div>
          <div>
            <div className="flex justify-between"><Label className="text-xs">Margem de lucro ideal</Label><span className="font-mono-nums text-sm font-semibold">{form.desired_profit_margin}%</span></div>
            <Slider className="mt-3" min={0} max={300} step={5} value={[parseFloat(form.desired_profit_margin) || 0]} onValueChange={([v]) => change({ desired_profit_margin: v })} data-testid="sim-margin-slider" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            {num("Impostos", "tax_percentage", 0.5, "%")}
            {num("Taxa cartão", "card_fee_percentage", 0.5, "%")}
            {num("Folha (variação)", "employees_pct_change", 5, "%")}
            {num("Variáveis (variação)", "variable_pct_change", 5, "%")}
          </div>
          {num("Novo custo fixo mensal (ex.: equipamento, financiamento)", "extra_fixed", 100, "R$")}
          <div>
            <Label className="text-xs">Custos fixos atuais</Label>
            <div className="space-y-2 mt-2">
              {data.fixed_costs.map((c) => (
                <div key={c.id} className="flex items-center gap-2">
                  <span className="text-sm text-slate-700 flex-1 truncate">{c.name}</span>
                  <Input type="number" step={50} value={form.fixed_costs[c.id]} onChange={(e) => change({ fixed_costs: { ...form.fixed_costs, [c.id]: e.target.value } })} className="w-32 font-mono-nums" data-testid={`sim-fixed-${c.id}`} />
                </div>
              ))}
              {!data.fixed_costs.length && <p className="text-xs text-slate-500">Nenhum custo fixo cadastrado.</p>}
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="grid sm:grid-cols-[1fr_auto_1fr] gap-4 items-center">
            <HourCard title="Hora clínica hoje" data={data.current} />
            <ArrowRight size={24} className="text-slate-400 mx-auto hidden sm:block" />
            <HourCard title="Hora clínica simulada" data={data.simulated} accent />
          </div>
          <div className={`card-crisp overflow-hidden transition-opacity ${loading ? "opacity-60" : ""}`} data-testid="sim-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr><th className="px-5 py-3 font-medium">Procedimento</th><th className="px-5 py-3 font-medium text-right">Preço ideal hoje</th><th className="px-5 py-3 font-medium text-right">Simulado</th><th className="px-5 py-3 font-medium text-right">Diferença</th></tr>
              </thead>
              <tbody>
                {data.procedures.map((p) => (
                  <tr key={p.id} className="border-t border-slate-100" data-testid={`sim-row-${p.id}`}>
                    <td className="px-5 py-3"><div className="font-medium text-slate-900">{p.name}</div><div className="text-xs text-slate-500">{p.category}</div></td>
                    <td className="px-5 py-3 text-right font-mono-nums text-slate-600">{formatCurrency(p.current)}</td>
                    <td className="px-5 py-3 text-right font-mono-nums font-semibold text-slate-900">{formatCurrency(p.simulated)}</td>
                    <td className="px-5 py-3 text-right"><Delta value={p.delta} pct={p.delta_pct} /></td>
                  </tr>
                ))}
                {!data.procedures.length && <tr><td colSpan={4} className="px-5 py-8 text-center text-slate-500">Cadastre procedimentos para simular.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
